import {Injectable} from '@angular/core';
import {Condition, ConditionGroup, ConditionOperator, SimpleCondition, SubCondition} from '../model/condition';
import {ConditionType} from '../enum/condition-type.enum';
import {DuplicableSectionEnum} from '../enum/duplicable-sections.enum';
import {EnrichedFieldValue} from '../model/field-value-context.interface';
import {TranslateService} from '@ngx-translate/core';
import {EformDuplicableEntityService} from './eform-duplicable-entity.service';
import {FlatField} from '../model/flat-field';
import {LogService} from '../../../shared/services/log.service';

/**
 * Service d'évaluation des conditions dans les formulaires eForm.
 *
 * Ce service gère l'évaluation des règles métier complexes :
 * - Conditions simples
 * - Groupes de conditions (AND/OR)
 * - Sous-conditions
 *
 * @version 1.0
 */
@Injectable({
  providedIn: 'root'
})
export class EformConditionEvaluatorService {

  private LOG: LogService = new LogService(EformConditionEvaluatorService);

  /** Cache des codes par mot-clé de codelist (postcode-country, nuts-country, …) */
  private codelistCodesCache: Map<string, string[]> = new Map();

  /** Référence à la fonction de récupération des flatFields (injectée) */
  private getFlatFieldsValue: () => Map<string, FlatField[]> = () => new Map();

  /** Référence à la fonction de récupération de l'index réel affiché (injectée) */
  private getDisplayedRealIndex: (type: string) => number | undefined = () => undefined;


  constructor(
    private translateService: TranslateService,
    private eformDuplicableEntityService: EformDuplicableEntityService
  ) {
  }

  /**
   * Configure la fonction de récupération des flatFields
   */
  setFlatFieldsProvider(provider: () => Map<string, FlatField[]>): void {
    this.getFlatFieldsValue = provider;
  }

  /**
   * Configure la fonction de récupération de l'index réel affiché
   */
  setDisplayedRealIndexProvider(provider: (type: string) => number | undefined): void {
    this.getDisplayedRealIndex = provider;
  }

  /**
   * Récupère l'index affiché pour un type d'élément duplicable.
   * Utilisé comme fallback quand displayedRealIndex n'est pas défini.
   */
  getDisplayedElementIndex(type: string): number {
    return this.eformDuplicableEntityService.getDisplayedElementIndex(type);
  }


  /**
   * Évalue une condition selon son type.
   */
  evaluateConditions(condition: Condition, elementId: string, repeatablePathForDuplicableElement: string): boolean {
    if ((condition as any).subCondition) {
      const simpleCondition = condition as SimpleCondition;
      const operator = simpleCondition.operator;

      // Pour les opérateurs IN/NOT_IN avec subCondition, on doit filtrer les valeurs
      // du champ cible (value) par la sous-condition AVANT d'évaluer la condition principale.
      // Exemple: not(BT-13714-Tender in BT-137-Lot[BT-681-Lot == 'true'])
      // signifie: BT-13714-Tender n'est pas dans les BT-137-Lot où BT-681-Lot == 'true'
      if (operator === ConditionType.IN || operator === ConditionType.NOT_IN) {
        const subCondition = (condition as any).subCondition;
        if (subCondition?.parentField && this.isField(simpleCondition.value)) {
          // Récupérer les valeurs filtrées par la sous-condition
          const filteredValues = this.getFilteredFieldValues(
            simpleCondition.value,
            subCondition,
            elementId,
            repeatablePathForDuplicableElement
          );

          // Évaluer la condition IN/NOT_IN avec les valeurs filtrées
          // 🔧 FIX 2026-06-01: Utiliser findCorrectFieldValue pour obtenir la valeur correcte
          // dans le contexte duplicable courant. Sans ce fix, getFieldValueForPath retourne
          // TOUTES les valeurs de BT-13713-LotResult (pour tous les lots results) car 'lotresult'
          // est dans ignoredElements. Résultat : si un seul LotResult n'a pas dps-list,
          // la condition IN est vraie pour TOUS les LotResults, cachant BT-119-LotResult partout.
          // findCorrectFieldValue utilise le duplicableMapping pour isoler la valeur du
          // LotResult courant (via elementId = "RES_0", "RES_1", etc.).
          const rawFieldValue = this.getFieldValueForPath(simpleCondition.field, repeatablePathForDuplicableElement);
          const fieldValue = this.findCorrectFieldValue(
            rawFieldValue,
            this.getFieldOccurrenceCount(simpleCondition.field),
            simpleCondition,
            elementId
          );

          if (operator === ConditionType.NOT_IN) {
            // NOT_IN: vrai si fieldValue n'est pas dans la liste filtrée
            if (this.isEmpty(filteredValues) || this.isEmpty(fieldValue)) {
              return true;
            }
            const fArray = this.toArray(fieldValue);
            const vArray = this.toArray(filteredValues);
            return fArray.every(fv => !vArray.includes(fv));
          } else {
            // IN: vrai si fieldValue est dans la liste filtrée
            // 🔧 FIX 2026-02-17: Gérer le cas où filteredValues contient '__EMPTY_MATCH__'
            // Cela signifie que la subCondition est satisfaite pour un élément avec valeur vide
            // Si fieldValue est aussi vide, c'est un match (cas "0 lauréat")
            const hasEmptyMatch = filteredValues.includes('__EMPTY_MATCH__');

            if (hasEmptyMatch && this.isEmpty(fieldValue)) {
              // Cas "0 lauréat": subCondition satisfaite (BT-142='clos-nw')
              // et les deux champs (OPT-320 et OPT-321) sont vides
              return true;
            }

            if (this.isEmpty(filteredValues) && this.isEmpty(fieldValue)) {
              return true;
            }
            if (this.isEmpty(filteredValues) || this.isEmpty(fieldValue)) {
              return false;
            }
            const fArray = this.toArray(fieldValue);
            const vArray = this.toArray(filteredValues).filter(v => v !== '__EMPTY_MATCH__');
            return vArray.some(v => fArray.includes(v));
          }
        }
      }

      // Logique existante pour les autres cas avec subCondition
      const evaluatedCondition = this.evaluateSimpleCondition(condition as SimpleCondition, elementId, repeatablePathForDuplicableElement);
      const isConditionOk = evaluatedCondition.isValid;
      let isSubConditionOk = false;

      if (isConditionOk) {
        if ((condition as any).duplicableSectionId && (condition as any).subCondition?.conditions?.conditions) {
          const duplicableSectionId = (condition as any).duplicableSectionId;
          const subConditionsArray = (condition as any).subCondition.conditions.conditions;
          subConditionsArray.forEach((subCond: any) => {
            subCond.duplicableSectionId = duplicableSectionId;
          });
          isSubConditionOk = this.evaluateConditions((condition as any).subCondition, elementId, repeatablePathForDuplicableElement);
        }

        return isSubConditionOk;
      } else if (!isConditionOk && (condition as any).field === 'OPT-210-Tenderer' && (condition as any).operator === 'IS_PRESENT') {
        return true;
      } else {
        return false;
      }
    }

    let result: boolean;
    switch (condition.type) {
      case 'simple':
        result = this.evaluateSimpleCondition(condition as SimpleCondition, elementId, repeatablePathForDuplicableElement).isValid;
        break;
      case 'group':
        result = this.evaluateConditionGroup(condition as ConditionGroup, elementId, repeatablePathForDuplicableElement);
        break;
      case 'subCondition':
        result = this.evaluateSubCondition(condition as SubCondition, elementId, repeatablePathForDuplicableElement);
        break;
      default:
        result = false;
    }

    return result;
  }

  /**
   * Évalue un groupe de conditions.
   */
  private evaluateConditionGroup(group: ConditionGroup, elementId: string, repeatablePathForDuplicableElement: string): boolean {
    const results = group.conditions.map((condition) => {
      if (group.duplicableSectionId) {
        (condition as any).duplicableSectionId = group.duplicableSectionId;
      }
      return this.evaluateConditions(condition, elementId, repeatablePathForDuplicableElement);
    });
    const finalResult = group.operator === ConditionOperator.AND
      ? results.every(result => result)
      : results.some(result => result);


    return finalResult;
  }

  /**
   * Évalue une sous-condition.
   */
  private evaluateSubCondition(subCondition: SubCondition, elementId: string, repeatablePathForDuplicableElement: string): boolean {
    const subConditions = subCondition.conditions.conditions;
    if (!subConditions) {
      return false;
    }
    return subConditions.every((subCondition) => this.evaluateConditions(subCondition, elementId, repeatablePathForDuplicableElement));
  }

  /**
   * Récupère les valeurs d'un champ filtrées par une sous-condition.
   * Utilisé pour les conditions IN/NOT_IN avec subCondition.
   * Exemple: BT-137-Lot[BT-681-Lot == 'true'] retourne uniquement les BT-137-Lot
   * dont le BT-681-Lot associé est égal à 'true'.
   *
   * @param fieldId L'identifiant du champ dont on veut les valeurs (ex: BT-137-Lot)
   * @param subCondition La sous-condition de filtrage
   * @param elementId L'identifiant de l'élément courant
   * @param repeatablePath Le path répétable pour le contexte
   * @returns Les valeurs filtrées du champ
   */
  private getFilteredFieldValues(
    fieldId: string,
    subCondition: SubCondition,
    elementId: string,
    repeatablePath: string
  ): any[] {
    const fields = this.getFlatFieldsValue().get(fieldId);
    if (!fields || fields.length === 0) {
      return [];
    }

    const filteredValues: any[] = [];
    const subConditions = subCondition.conditions?.conditions;

    if (!subConditions || subConditions.length === 0) {
      // Pas de sous-condition, retourner toutes les valeurs
      return fields
        .filter(f => f.value !== undefined && f.value !== null && f.value !== '')
        .map(f => f.value);
    }

    // Pour chaque valeur du champ parent, vérifier si les sous-conditions sont satisfaites
    fields.forEach((field, index) => {
      // 🔧 FIX 2026-02-17: Ne pas ignorer les valeurs vides immédiatement
      // Dans le cas "0 lauréat", OPT-320-LotResult est vide mais on doit quand même
      // vérifier si BT-142-LotResult = 'clos-nw' pour cet index
      const fieldValueIsEmpty = field.value === undefined || field.value === null || field.value === '';

      // Vérifier si toutes les sous-conditions sont satisfaites pour cet index
      const allSubConditionsMet = subConditions.every((subCond: any) => {
        const subCondField = subCond.field;
        if (!subCondField) return true;

        // Récupérer la valeur du champ de la sous-condition au même index
        const subCondFields = this.getFlatFieldsValue().get(subCondField);
        // 🔧 FIX 2026-02-23: Si le champ de la sous-condition n'existe pas ou est vide,
        // la sous-condition ne peut pas être satisfaite (retourner false au lieu de true).
        // Exemple: Si BT-681-Lot n'existe pas, la condition "BT-681-Lot == 'true'" est forcément fausse.
        if (!subCondFields || subCondFields.length === 0) return false;

        // Trouver la valeur correspondant au même élément parent (même index ou même path parent)
        let matchingSubCondField = subCondFields.find(sf => {
          // Correspondance par index duplicable
          if (sf.duplicableIndex !== undefined && field.duplicableIndex !== undefined) {
            return sf.duplicableIndex === field.duplicableIndex;
          }
          // Correspondance par path parent similaire
          if (sf.path && field.path) {
            const sfParentPath = this.extractParentPath(sf.path);
            const fieldParentPath = this.extractParentPath(field.path);
            return sfParentPath === fieldParentPath;
          }
          return false;
        });

        // Fallback: utiliser l'index direct si pas de correspondance trouvée
        if (!matchingSubCondField && subCondFields[index]) {
          matchingSubCondField = subCondFields[index];
        }

        // 🔧 FIX 2026-02-23: Si aucun champ correspondant n'est trouvé pour la sous-condition,
        // cela signifie que la valeur de la sous-condition n'est pas définie pour cet élément.
        // Dans ce cas, la sous-condition ne peut PAS être satisfaite (retourner false).
        // Exemple: Si BT-681-Lot n'est pas rempli pour un lot, la condition
        // "BT-681-Lot == 'true'" ne peut pas être vraie pour ce lot.
        // Avant ce fix, retourner true causait l'affichage erroné de BT-682-Tender
        // pour des offres liées à des lots sans BT-681-Lot défini.
        if (!matchingSubCondField) return false;

        // Évaluer la sous-condition avec la valeur trouvée
        const subCondValue = matchingSubCondField.value;
        const operator = subCond.operator;
        const expectedValue = subCond.value;

        const result = this.evaluateSubConditionValue(subCondValue, operator, expectedValue);


        return result;
      });

      if (allSubConditionsMet) {
        // 🔧 FIX 2026-02-17: Si la subCondition est satisfaite mais la valeur est vide,
        // on ajoute un marqueur spécial pour indiquer qu'il y a un "match" avec valeur vide
        // Cela permet au cas "0 lauréat" de fonctionner : OPT-320-LotResult est vide
        // mais BT-142-LotResult = 'clos-nw', donc l'offre devrait être interdite
        if (fieldValueIsEmpty) {
          filteredValues.push('__EMPTY_MATCH__');
        } else {
          filteredValues.push(field.value);
        }
      }
    });

    return filteredValues;
  }

  /**
   * Extrait le chemin parent d'un path (tout sauf le dernier segment).
   */
  private extractParentPath(path: string): string {
    if (!path) return '';
    const lastDot = path.lastIndexOf('.');
    return lastDot > 0 ? path.substring(0, lastDot) : path;
  }

  /**
   * Évalue une valeur selon un opérateur et une valeur attendue.
   */
  private evaluateSubConditionValue(actualValue: any, operator: ConditionType, expectedValue: any): boolean {
    const normalizedExpected = ('' + expectedValue).replace(/^'(.*)'$/, '$1').toLowerCase();
    const normalizedActual = actualValue !== null && actualValue !== undefined
      ? ('' + actualValue).toLowerCase()
      : '';

    switch (operator) {
      case ConditionType.EQUALS:
        return normalizedActual === normalizedExpected;
      case ConditionType.NOT_EQUALS:
        return normalizedActual !== normalizedExpected;
      case ConditionType.IS_PRESENT:
        return !this.isEmpty(actualValue);
      case ConditionType.IS_NOT_PRESENT:
        return this.isEmpty(actualValue);
      case ConditionType.IN: {
        // 🔧 FIX 2026-02-20: Gérer l'opérateur IN dans les sous-conditions
        // Vérifie si actualValue est dans la liste expectedValue
        if (this.isEmpty(actualValue)) {
          return false;
        }
        const expectedArray = this.isArrayString(expectedValue)
          ? this.parseArrayString(expectedValue).map(v => v.toLowerCase())
          : [normalizedExpected];
        return expectedArray.includes(normalizedActual);
      }
      case ConditionType.NOT_IN: {
        // 🔧 FIX 2026-02-20: Gérer l'opérateur NOT_IN dans les sous-conditions
        // Vérifie si actualValue n'est PAS dans la liste expectedValue
        // Pour BT-765-Lot NOT_IN ['fa-mix', 'fa-w-rc', 'fa-wo-rc']
        if (this.isEmpty(actualValue)) {
          return true; // Une valeur vide n'est pas dans la liste
        }
        const expectedArray = this.isArrayString(expectedValue)
          ? this.parseArrayString(expectedValue).map(v => v.toLowerCase())
          : [normalizedExpected];
        return !expectedArray.includes(normalizedActual);
      }
      default:
        return true;
    }
  }

  /**
   * Évalue une condition simple.
   */
  private evaluateSimpleCondition(condition: SimpleCondition, elementId: string, repeatablePathForDuplicableElement?: string): SimpleCondition {
    let fieldValue = this.getFieldValueForPath(condition.field, repeatablePathForDuplicableElement);

    if (Array.isArray(fieldValue) && condition.parentIndex !== undefined) {
      fieldValue = fieldValue[condition.parentIndex];
    }
    const fieldOccurrenceCount = this.getFieldOccurrenceCountForPath(condition.field, repeatablePathForDuplicableElement);



    if (condition.field.includes(' - ') || condition.field.includes(' + ')) {
      const conditionField = condition.field.replace(/^\((.*)\)$/, '$1').trim();
      const operator = conditionField.includes(' - ') ? ' - ' : ' + ';
      const fields = conditionField.split(operator);

      if (fields.length === 2) {
        const firstFieldValue = this.getFieldValue(fields[0].trim());
        const secondFieldValue = this.getFieldValue(fields[1].trim());

        if (firstFieldValue !== undefined && firstFieldValue !== null && secondFieldValue !== undefined && secondFieldValue !== null) {
          const firstDate = new Date(firstFieldValue);
          const secondDate = new Date(secondFieldValue);
          const isFirstDateValid = !isNaN(firstDate.getTime());
          const isSecondDateValid = !isNaN(secondDate.getTime());

          let result: number | string;

          if (isFirstDateValid && isSecondDateValid) {
            result = operator === ' - '
              ? Math.floor((firstDate.getTime() - secondDate.getTime()) / (1000 * 60 * 60 * 24))
              : Math.floor((firstDate.getTime() + secondDate.getTime()) / (1000 * 60 * 60 * 24));
          } else if (!isNaN(Number(firstFieldValue)) && !isNaN(Number(secondFieldValue))) {
            const a = Number(firstFieldValue);
            const b = Number(secondFieldValue);
            result = operator === ' - ' ? a - b : a + b;
          }
          return this.compareValues(result, fieldOccurrenceCount, condition, elementId, null);
        } else {
          condition.isValid = true;
          return condition;
        }
      }
    }
    return this.compareValues(fieldValue, fieldOccurrenceCount, condition, elementId, condition.field);
  }

  /**
   * Récupère les valeurs d'un champ en filtrant par le path répétable.
   * Cela permet de ne récupérer que les valeurs du même contexte répétable.
   * @param inputId L'identifiant du champ
   * @param repeatablePath Le path répétable pour filtrer les valeurs
   */
  public getFieldValueForPath(inputId: string, repeatablePath?: string): any | any[] {
    const fields = this.getFlatFieldsValue().get(inputId);

    // Si pas de fields ou un seul field, pas besoin de filtrer
    if (!fields || fields.length <= 1) {
      return this.getFieldValue(inputId);
    }

    // 🔧 FIX 2026-02-23: Extraire l'index des éléments répétables imbriqués depuis le repeatablePath
    // Pour les champs au sein de subordinateawardingcriterion, awardcriterionparameter, etc.
    // on doit filtrer par leur index spécifique pour éviter les données fantômes après duplication d'avis
    if (repeatablePath) {
      const nestedIndices = this.extractNestedRepeatableIndices(repeatablePath);

      if (Object.keys(nestedIndices).length > 0) {
        // Filtrer les champs dont les indices imbriqués correspondent
        const filteredByNestedIndex = fields.filter(field => {
          if (!field.path) return false;
          const fieldIndices = this.extractNestedRepeatableIndices(field.path);

          // Vérifier que tous les indices du contexte correspondent
          for (const [key, value] of Object.entries(nestedIndices)) {
            if (fieldIndices[key] !== undefined && fieldIndices[key] !== value) {
              return false;
            }
          }
          return true;
        });


        if (filteredByNestedIndex.length > 0) {
          const values = filteredByNestedIndex
            .filter(field => field.value !== undefined && field.value !== null && field.value !== '')
            .map(field => field.value);

          return values.length > 1 ? values : (values.length === 1 ? values[0] : null);
        }
      }
    }

    // Si pas de repeatablePath ou pas d'indices imbriqués, comportement par défaut
    if (!repeatablePath) {
      return this.getFieldValue(inputId);
    }

    // Extraire les indices du contexte
    const contextIndices = this.extractAllArrayIndices(repeatablePath);

    // Si pas d'indices imbriqués dans le contexte, vérifier si les fields ont des listNames différents
    // Les champs avec listName différent ne doivent pas être filtrés par index
    const uniqueListNames = new Set(fields.map(f => f.listName).filter(ln => ln !== undefined));
    if (uniqueListNames.size > 1) {
      // Les fields ont des listNames différents, ne pas filtrer par index
      // car ce sont des champs logiquement différents (ex: number-fixed vs number-threshold)
      return this.getFieldValue(inputId);
    }

    if (Object.keys(contextIndices).length === 0) {
      return this.getFieldValue(inputId);
    }

    // Vérifier si les fields ont des indices différents pour les éléments répétables
    const fieldsIndices = fields.map(f => this.extractAllArrayIndices(f.path));
    const hasMultipleIndices = this.hasMultipleDifferentIndices(fieldsIndices);

    // Si tous les fields ont les mêmes indices (ou pas d'indices), pas besoin de filtrer
    if (!hasMultipleIndices) {
      return this.getFieldValue(inputId);
    }

    // Filtrer les champs dont les indices correspondent au contexte
    const filteredFields = fields.filter(field => {
      return this.doArrayIndicesMatch(field.path, contextIndices);
    });

    // Si aucun champ filtré n'est trouvé
    if (filteredFields.length === 0) {
      // Vérifier si des champs avec des valeurs existent (pour éviter de perdre des données)
      const fieldsWithValues = fields.filter(f =>
        f.value !== undefined && f.value !== null && f.value !== ''
      );

      // Si des valeurs existent mais ne correspondent pas aux indices,
      // c'est peut-être un problème de format de path, utiliser le comportement par défaut
      if (fieldsWithValues.length > 0) {
        return this.getFieldValue(inputId);
      }

      // Aucune valeur nulle part, retourner null
      return null;
    }

    const values = filteredFields
      .filter(field => field.value !== undefined && field.value !== null && field.value !== '')
      .map(field => field.value);

    return values.length > 1 ? values : (values.length === 1 ? values[0] : null);
  }

  /**
   * 🔧 FIX 2026-02-23: Détermine le type d'élément duplicable depuis un inputId

  /**
   * Vérifie si les indices sont différents entre les éléments.
   */
  private hasMultipleDifferentIndices(indicesArray: Record<string, number>[]): boolean {
    if (indicesArray.length <= 1) return false;

    // Collecter tous les types de tableaux présents
    const allArrayTypes = new Set<string>();
    indicesArray.forEach(indices => {
      Object.keys(indices).forEach(key => allArrayTypes.add(key));
    });

    // Pour chaque type de tableau, vérifier si les indices sont différents
    for (const arrayType of allArrayTypes) {
      const indicesForType = new Set<number>();
      indicesArray.forEach(indices => {
        if (arrayType in indices) {
          indicesForType.add(indices[arrayType]);
        }
      });
      // Si plus d'un indice différent pour ce type, retourner true
      if (indicesForType.size > 1) {
        return true;
      }
    }

    return false;
  }

  /**
   * Compte le nombre d'occurrences d'un champ en filtrant par le contexte parent du path répétable.
   * Pour les opérateurs COUNT_*, il faut compter les siblings (ex: tous les tenderers d'un même TPA)
   * et non filtrer par l'index de l'élément courant.
   *
   * 🔧 FIX 2026-03-17: Le matchingField DOIT contenir le leaf dans son path pour être considéré valide ;
   * sinon le champ compté vit à un niveau supérieur et on fallback sur le repeatablePath.
   * Le fallback (quand le match exact échoue) vérifie aussi les indices parent pour éviter
   * de trouver un champ d'un autre contexte parent (ex: tenderer du TPA 0 au lieu du TPA 1)
   * grâce à setDisplayedRealIndex qui garantit la cohérence des paths dans les flatFields.
   *
   * @param inputId L'identifiant du champ
   * @param repeatablePath Le path répétable pour filtrer
   */
  private getFieldOccurrenceCountForPath(inputId: string, repeatablePath?: string): number {
    const fields = this.getFlatFieldsValue().get(inputId);
    if (!fields || fields.length === 0) {
      return 0;
    }

    // Si pas de repeatablePath ou un seul field, count global
    if (!repeatablePath || fields.length <= 1) {
      return fields.length;
    }

    // Extraire TOUS les indices répétables (y compris entités duplicables)
    const allIndices = this.extractAllRepeatableIndicesForCount(repeatablePath);

    // 🔧 FIX 2026-03-17: Corriger les indices des entités duplicables dans le repeatablePath
    // Le repeatablePath contient toujours l'index local 0 (car on ne charge qu'un élément à la fois),
    // mais les flatFields ont le vrai index dans leur path (via updatePathWithRealIndex).
    // Sans cette correction, le comptage COUNT_* pour un TPA/ORG/etc. d'index > 0 échoue car
    // allIndices.tenderingparty=0 ne match pas les flatFields qui ont tenderingparty.1 dans leur path.
    this.correctIndicesWithDisplayedRealIndex(allIndices);

    if (Object.keys(allIndices).length === 0) {
      return fields.length;
    }

    // Trouver l'élément le plus profond (leaf) dans le repeatablePath
    // C'est l'élément par lequel on NE doit PAS filtrer pour compter les siblings
    const leafElement = this.findDeepestRepeatableElement(repeatablePath, Object.keys(allIndices));

    if (!leafElement || allIndices[leafElement] === undefined) {
      return fields.length;
    }

    const leafIndex = allIndices[leafElement];

    // Étape 1 : trouver un flatField qui correspond au leaf, pour en dériver le vrai parent.
    // Le flatField DOIT contenir le leaf dans son path (sinon le champ vit plus haut dans l'arbre).
    // On tente d'abord un match exact (leaf + tous les indices parent),
    // puis un fallback sur le leaf seul (cas où le repeatablePath a un index parent erroné).
    let matchingField = fields.find(field => {
      if (!field.path) return false;
      const fi = this.extractAllRepeatableIndicesForCount(field.path);
      // Le field DOIT contenir le leaf pour être un match valide
      if (fi[leafElement] === undefined) return false;
      return Object.entries(allIndices).every(([k, v]) => fi[k] === undefined || fi[k] === v);
    });

    if (!matchingField) {
      // Fallback : matcher par le leaf avec validation des indices parent
      // pour éviter de trouver un champ d'un contexte parent différent
      // (ex: tenderer du TPA 0 quand on évalue le TPA 1)
      matchingField = fields.find(field => {
        if (!field.path) return false;
        const fi = this.extractAllRepeatableIndicesForCount(field.path);
        return fi[leafElement] === leafIndex;
      });
    }

    // Étape 2 : construire les indices parent pour le filtrage
    let actualParentIndices: Record<string, number> = {};
    if (matchingField) {
      // Extraire les vrais indices parent depuis le path réel du flatField trouvé
      const actualFieldIndices = this.extractAllRepeatableIndicesForCount(matchingField.path);
      actualParentIndices = { ...actualFieldIndices };
      delete actualParentIndices[leafElement];
    } else {
      // Le champ compté ne contient pas le leaf (il vit à un niveau supérieur)
      // → fallback sur les indices du repeatablePath sans le leaf
      actualParentIndices = { ...allIndices };
      delete actualParentIndices[leafElement];
    }

    // Si pas d'indices parent restants, retourner le count global
    if (Object.keys(actualParentIndices).length === 0) {
      return fields.length;
    }

    // Filtrer les champs par le contexte parent réel
    const filteredFields = fields.filter(field => {
      if (!field.path) return false;
      const fieldIndices = this.extractAllRepeatableIndicesForCount(field.path);
      for (const [key, value] of Object.entries(actualParentIndices)) {
        if (fieldIndices[key] !== undefined && fieldIndices[key] !== value) {
          return false;
        }
      }
      return true;
    });

    // Ne logger que quand le filtrage a réellement changé le count (sinon c'est du bruit)
    if (filteredFields.length !== fields.length) {
      this.LOG.debug(`[getFieldOccurrenceCountForPath] inputId=${inputId} | ` +
        `leaf=${leafElement} actualParentIndices=${JSON.stringify(actualParentIndices)} | ` +
        `matchedFromField=${!!matchingField} | ` +
        `totalFields=${fields.length} → filteredCount=${filteredFields.length}`);
    }

    return filteredFields.length;
  }

  /**
   * 🔧 FIX 2026-03-17: Extrait TOUS les indices répétables d'un path, y compris les entités duplicables.
   * Utilisé uniquement pour le comptage (COUNT_*), pas pour le filtrage de valeurs.
   * Exclut seulement les éléments purement techniques (UBL extensions, multilingues).
   */
  private extractAllRepeatableIndicesForCount(path: string): Record<string, number> {
    if (!path) return {};

    const indices: Record<string, number> = {};
    const normalizedPath = path.toLowerCase();

    // Éléments purement techniques à ignorer
    const technicalIgnored = [
      'ublextension', 'ublextensions', 'extensioncontent', 'eformsextension',
      'description', 'name', 'fieldsprivacy', 'fieldidentifiercode'
    ];

    let match;

    // Pattern pour format "element[]0" (repeatablePath format)
    const bracketPattern = /([a-z_]+)\[\](\d+)/gi;
    while ((match = bracketPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (!technicalIgnored.includes(elementName)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    // Pattern pour format "element.0" (flatField path format)
    const dotPattern = /([a-z_]+)\.(\d+)/gi;
    while ((match = dotPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (!technicalIgnored.includes(elementName) && !(elementName in indices)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    // Pattern pour format "element_INDEX_0"
    const indexPattern = /([a-z_]+)_INDEX_(\d+)/gi;
    while ((match = indexPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (!technicalIgnored.includes(elementName) && !(elementName in indices)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    return indices;
  }

  /**
   * 🔧 FIX 2026-03-17: Trouve l'élément répétable le plus profond (position la plus avancée dans le path)
   * parmi les candidats. Cet élément "leaf" ne sera pas utilisé comme filtre pour le comptage.
   */
  private findDeepestRepeatableElement(path: string, candidates: string[]): string | null {
    if (!path || candidates.length === 0) return null;

    const normalizedPath = path.toLowerCase();
    let deepestElement: string | null = null;
    let deepestPosition = -1;

    for (const candidate of candidates) {
      const patterns = [
        new RegExp(`${candidate}\\[\\]\\d+`, 'i'),
        new RegExp(`${candidate}\\.\\d+`, 'i'),
        new RegExp(`${candidate}\\[\\d+\\]`, 'i'),
        new RegExp(`${candidate}_INDEX_\\d+`, 'i')
      ];

      for (const pattern of patterns) {
        const match = pattern.exec(normalizedPath);
        if (match && match.index > deepestPosition) {
          deepestPosition = match.index;
          deepestElement = candidate;
        }
      }
    }

    return deepestElement;
  }

  /**
   * 🔧 FIX 2026-03-17: Corrige les indices des entités duplicables dans un Record d'indices.
   * Le repeatablePath contient toujours l'index local (souvent 0) car on ne charge qu'un
   * élément duplicable à la fois. Mais les flatFields stockent le vrai index (via updatePathWithRealIndex).
   * Cette méthode aligne les indices du repeatablePath sur les vrais indices affichés.
   *
   * @param indices Le Record mutable d'indices à corriger (modifié en place)
   */
  private correctIndicesWithDisplayedRealIndex(indices: Record<string, number>): void {
    // Mapping nom d'élément dans le path → code du type duplicable
    const elementToDuplicableType: Record<string, string> = {
      'tenderingparty': 'TPA',
      'organization': 'ORG',
      'lotresult': 'RES',
      'lottender': 'TEN',
      'tender': 'TEN',
      'settledcontract': 'CON',
      'procurementprojectlot': 'LOT'
    };

    for (const [element, type] of Object.entries(elementToDuplicableType)) {
      if (element in indices) {
        const realIndex = this.getDisplayedRealIndex(type);
        if (realIndex !== undefined && realIndex !== indices[element]) {
          this.LOG.debug(`[correctIndicesWithDisplayedRealIndex] ${element}: ${indices[element]} → ${realIndex} (type=${type})`);
          indices[element] = realIndex;
        }
      }
    }
  }


  /**
   * Extrait tous les indices des tableaux depuis un path.
   * Gère différents formats: element.0, element[]0, element[0], element_INDEX_0
   * Ne capture que les éléments répétables imbriqués, pas les éléments duplicables principaux
   * qui sont déjà gérés par le système de duplication (Lot, Organisation, etc.)
   * @param path Le path à analyser
   * @returns Un objet avec les noms des tableaux comme clés et leurs indices comme valeurs
   */
  private extractAllArrayIndices(path: string): Record<string, number> {
    if (!path) {
      return {};
    }

    const indices: Record<string, number> = {};
    const normalizedPath = path.toLowerCase();

    // Éléments répétables imbriqués (au sein d'un élément duplicable principal)
    // Ces éléments doivent être filtrés par index pour les conditions
    // Note: 'description' et 'name' sont exclus car ce sont des tableaux multilingues
    // avec généralement un seul élément, et leur filtrage causerait des faux négatifs
    // Note: Les éléments duplicables principaux (lotresult, organization, tender, etc.)
    // ne sont PAS inclus car ils sont gérés par le système de duplication et leurs index
    // peuvent différer entre le formulaire local (toujours 0) et le backend (index réel).
    const nestedRepeatableElements = [
      'subordinateawardingcriterion',
      'awardingcriterion',
      'awardcriterionparameter',
      'tendererqualificationrequest',
      'specifictendererequirement',
      'ultimatebeneficialowner',
      'partylegalentity',
      'partyname'
    ];

    let match;

    // Pattern pour format "element.0" (flatField format)
    const dotPattern = /([a-z_]+)\.(\d+)/gi;
    while ((match = dotPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (nestedRepeatableElements.includes(elementName)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    // Pattern pour format "element[]0" (repeatablePath format)
    const bracketPattern = /([a-z_]+)\[\](\d+)/gi;
    while ((match = bracketPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (nestedRepeatableElements.includes(elementName)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    // Pattern pour format "element[0]"
    const bracketWithIndexPattern = /([a-z_]+)\[(\d+)\]/gi;
    while ((match = bracketWithIndexPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (nestedRepeatableElements.includes(elementName)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    // Pattern pour format "element_INDEX_0"
    const indexPattern = /([a-z_]+)_INDEX_(\d+)/gi;
    while ((match = indexPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (nestedRepeatableElements.includes(elementName)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    return indices;
  }

  /**
   * 🔧 FIX 2026-03-17: Extrait les indices des éléments répétables depuis un path.
   * Exclut uniquement les éléments techniques (toujours index 0) et multilingues.
   * @param path Le path à analyser
   * @returns Un objet avec les noms des tableaux comme clés et leurs indices comme valeurs
   */
  private extractNestedRepeatableIndices(path: string): Record<string, number> {
    if (!path) {
      return {};
    }

    const indices: Record<string, number> = {};
    const normalizedPath = path.toLowerCase();

    // Éléments à IGNORER car leurs indices ne sont pas significatifs pour le filtrage :
    // - Éléments duplicables principaux (gérés par le système de duplication, pas par le path)
    // - Éléments techniques UBL (toujours index 0)
    // - Éléments multilingues
    const ignoredElements = [
      // Éléments duplicables principaux - leurs indices sont gérés séparément
      'lotresult',
      'tender',
      'lottender',
      'organization',
      'tenderingparty',
      'settledcontract',
      // Éléments techniques UBL
      'ublextension',
      'ublextensions',
      'extensioncontent',
      'eformsextension',
      // Éléments multilingues et techniques
      'description',
      'name',
      'fieldsprivacy',
      'fieldidentifiercode'
    ];

    let match;

    // Pattern pour format "element[]0" (repeatablePath format)
    const bracketPattern = /([a-z_]+)\[\](\d+)/gi;
    while ((match = bracketPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (!ignoredElements.includes(elementName)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    // Pattern pour format "element.0" (flatField format)
    const dotPattern = /([a-z_]+)\.(\d+)/gi;
    while ((match = dotPattern.exec(normalizedPath)) !== null) {
      const elementName = match[1].toLowerCase();
      if (!ignoredElements.includes(elementName) && !(elementName in indices)) {
        indices[elementName] = parseInt(match[2], 10);
      }
    }

    return indices;
  }

  /**
   * Vérifie si les indices des tableaux d'un champ correspondent au contexte.
   * Compare uniquement les tableaux présents dans les deux paths.
   * @param fieldPath Le path du champ à vérifier
   * @param contextIndices Les indices du contexte répétable
   * @returns true si les indices correspondent
   */
  private doArrayIndicesMatch(fieldPath: string, contextIndices: Record<string, number>): boolean {
    const fieldIndices = this.extractAllArrayIndices(fieldPath);

    // Pour chaque tableau dans le contexte, vérifier si l'indice correspond dans le champ
    for (const [arrayName, contextIndex] of Object.entries(contextIndices)) {
      if (arrayName in fieldIndices) {
        if (fieldIndices[arrayName] !== contextIndex) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Récupère les valeurs d'un champ par son inputId.
   * @param inputId L'identifiant du champ
   * @param filterListName Si spécifié, ne retourne que les valeurs avec ce listName
   */
  getFieldValue(inputId: string, filterListName?: string): any | any[] {
    const enrichedValue = this.getEnrichedFieldValue(inputId, filterListName);

    return enrichedValue.values;
  }

  /**
   * Compte le nombre d'occurrences d'un champ avec un inputId donné.
   */
  getFieldOccurrenceCount(inputId: string): number {
    const fields = this.getFlatFieldsValue().get(inputId);
    if (!fields) {
      return 0;
    }

    // Debug log pour BT-165-Organization-Company

    return fields.length;
  }

  /**
   * Récupère les valeurs d'un champ avec leur contexte d'élément duplicable.
   * @param inputId L'identifiant du champ
   * @param filterListName Si spécifié, filtre les champs par ce listName
   */
  private getEnrichedFieldValue(inputId: string, filterListName?: string): EnrichedFieldValue {
    let fields = this.getFlatFieldsValue().get(inputId);


    if (!fields || fields.length === 0) {
      return {values: null, isDuplicable: false};
    }

    // Filtrer par listName si spécifié
    if (filterListName && fields.length > 1) {
      const filtered = fields.filter(f => f.listName === filterListName);
      if (filtered.length > 0) {
        fields = filtered;
      }
    }

    const fieldsWithValues = this.getFieldsWithValuesSorted(fields);

    if (fieldsWithValues.length === 0) {
      return {values: null, isDuplicable: false};
    }

    const isDuplicable = fieldsWithValues.some(field => field.isDuplicableField);

    if (!isDuplicable) {
      return this.processNonDuplicableFields(fieldsWithValues);
    }

    return this.createDuplicableMapping(fieldsWithValues);
  }

  private getFieldsWithValuesSorted(fields: FlatField[]): FlatField[] {
    return fields
      .filter(field => field && field.value !== undefined)
      .sort((a, b) => {
        if (!a || !b) return 0;
        if (a.duplicableIndex !== undefined && b.duplicableIndex !== undefined) {
          return a.duplicableIndex - b.duplicableIndex;
        }
        if (a.duplicableIndex !== undefined) return -1;
        if (b.duplicableIndex !== undefined) return 1;
        return 0;
      });
  }

  private processNonDuplicableFields(fieldsWithValues: FlatField[]): EnrichedFieldValue {
    const values = fieldsWithValues.map(field => field.value);
    return {
      values: values.length > 1 ? values : values[0],
      isDuplicable: false
    };
  }

  private createDuplicableMapping(fieldsWithValues: FlatField[]): EnrichedFieldValue {
    const duplicableMapping = new Map<string, any[]>();
    const allValues: any[] = [];

    fieldsWithValues.forEach(field => {
      allValues.push(field.value);

      if (field.duplicableType && field.duplicableIndex !== undefined) {
        const elementId = `${field.duplicableType}_${field.duplicableIndex}`;

        if (!duplicableMapping.has(elementId)) {
          duplicableMapping.set(elementId, []);
        }
        duplicableMapping.get(elementId)!.push(field.value);
      }
    });


    return {
      values: allValues.length > 1 ? allValues : allValues[0],
      duplicableMapping,
      isDuplicable: true
    };
  }

  /**
   * Compare les valeurs selon l'opérateur de condition.
   */
  private compareValues(fieldValue: any, fieldOccurrenceCount: any, condition: SimpleCondition, elementId: string, fieldIdForUnit?: string | null): SimpleCondition {
    fieldValue = this.findCorrectFieldValue(fieldValue, fieldOccurrenceCount, condition, elementId);

    // 🔧 FIX 2026-03-17: Ne pas écraser fieldOccurrenceCount pour les opérateurs COUNT_*
    // Pour COUNT, le count doit venir de getFieldOccurrenceCountForPath (qui compte les siblings au niveau parent)
    // et non pas de fieldValue.length (qui est filtré par l'index de l'élément courant)
    if (!this.isCountOperator(condition.operator)) {
      if (Array.isArray(fieldValue)) {
        fieldOccurrenceCount = fieldValue.length;
      } else {
        fieldOccurrenceCount = 1;
      }
    }

    const operator = condition.operator;
    const conditionValue = condition.value;
    let value;

    if (this.isArrayString(conditionValue)) {
      value = this.parseArrayString(conditionValue);
    } else if (this.isPeriodValue(conditionValue)) {
      value = this.parsePeriod(conditionValue);
      if (fieldIdForUnit) {
        fieldValue = this.normalizeMeasuredFieldIfPeriodRight(fieldIdForUnit, fieldValue, true);
      }
      if (fieldValue == null) {
        condition.isValid = true;
        return condition;
      }
    } else if (this.isField(conditionValue)) {
      value = this.getFieldValue(conditionValue);
      const rightFieldOccurrenceCount = this.getFieldOccurrenceCount(conditionValue);
      const rightCondition: SimpleCondition = {
        ...condition,
        field: conditionValue
      };
      value = this.findCorrectFieldValue(value, rightFieldOccurrenceCount, rightCondition, elementId);
    } else if (this.isCodelistKeyword(conditionValue)) {
      // Résolu ici, en amont du switch, pour que tous les opérateurs (IN, NOT_IN…)
      // reçoivent la liste de codes et non le nom de la codelist.
      value = this.getCodelistCountryCodes(conditionValue as string);
    } else {
      value = conditionValue;
    }

    switch (operator) {
      case ConditionType.IS_PRESENT:
        condition.isValid = Array.isArray(fieldValue) ? fieldValue.some(v => !this.isEmpty(v)) : !this.isEmpty(fieldValue);
        break;
      case ConditionType.IS_NOT_PRESENT:
        condition.isValid = Array.isArray(fieldValue) ? fieldValue.every(v => this.isEmpty(v)) : this.isEmpty(fieldValue);
        break;
      case ConditionType.IN: {
        if (this.isEmpty(value) && this.isEmpty(fieldValue)) {
          condition.isValid = true;
          break;
        }
        if (this.isEmpty(value) || this.isEmpty(fieldValue)) {
          condition.isValid = false;
          break;
        }
        // 🔧 FIX 2026-02-20: Parser correctement la valeur si c'est une chaîne de tableau
        // Ex: "['fa-mix', 'fa-w-rc', 'fa-wo-rc']" doit être parsé en tableau
        const vArray = this.isArrayString(value) ? this.parseArrayString(value) : this.toArray(value);
        const fArray = this.toArray(fieldValue);
        let inIndex = vArray.findIndex(v => fArray.includes(v));
        condition.isValid = inIndex !== -1;
        if (condition.subCondition) {
          condition.subCondition.conditions.conditions.forEach(sub => {
            (sub as SimpleCondition).parentIndex = inIndex;
          });
        }
        break;
      }
      case ConditionType.NOT_IN: {
        if (this.isEmpty(value) || this.isEmpty(fieldValue)) {
          condition.isValid = true;
          break;
        }
        // 🔧 FIX 2026-02-20: Parser correctement la valeur si c'est une chaîne de tableau
        // Ex: "['fa-mix', 'fa-w-rc', 'fa-wo-rc']" doit être parsé en tableau
        const vArray = this.isArrayString(value) ? this.parseArrayString(value) : this.toArray(value);
        const fArray = this.toArray(fieldValue);

        condition.isValid = fArray.every(fv => !vArray.includes(fv));
        break;
      }
      case ConditionType.EQUALS: {
        const v = ('' + value).replace(/^'(.*)'$/, '$1').toLowerCase();
        const fieldValuesArrayEq = this.toArray(fieldValue);
        condition.isValid = fieldValuesArrayEq.some(fv => ('' + fv).toLowerCase() === v);
        break;
      }
      case ConditionType.NOT_EQUALS: {
        const v = ('' + value).replace(/^'(.*)'$/, '$1').toLowerCase();
        const fieldValuesArray = this.toArray(fieldValue);
        condition.isValid = fieldValuesArray.every(fv => ('' + fv).toLowerCase() !== v);
        break;
      }
      case ConditionType.GREATER_THAN:
        condition.isValid = this.toArray(fieldValue).map(fv => fv != null ? +fv : NaN).some(fv => !isNaN(fv) && fv > +value);
        break;
      case ConditionType.LESS_THAN:
        condition.isValid = this.toArray(fieldValue).map(fv => fv != null ? +fv : NaN).some(fv => !isNaN(fv) && fv < +value);
        break;
      case ConditionType.GREATER_THAN_OR_EQUAL:
        condition.isValid = this.toArray(fieldValue).map(fv => fv != null ? +fv : NaN).some(fv => !isNaN(fv) && fv >= +value);
        break;
      case ConditionType.LESS_THAN_OR_EQUAL:
        condition.isValid = this.toArray(fieldValue).map(fv => fv != null ? +fv : NaN).some(fv => !isNaN(fv) && fv <= +value);
        break;
      case ConditionType.COUNT_EQUALS:
        condition.isValid = fieldOccurrenceCount === +value;
        break;
      case ConditionType.COUNT_NOT_EQUALS:
        condition.isValid = fieldOccurrenceCount !== +value;
        break;
      case ConditionType.COUNT_LESS_THAN:
        condition.isValid = fieldOccurrenceCount < +value;
        break;
      case ConditionType.COUNT_LESS_THAN_OR_EQUAL:
        condition.isValid = fieldOccurrenceCount <= +value;
        break;
      case ConditionType.COUNT_GREATER_THAN:
        condition.isValid = fieldOccurrenceCount > +value;
        break;
      case ConditionType.COUNT_GREATER_THAN_OR_EQUAL:
        condition.isValid = fieldOccurrenceCount >= +value;
        break;
      default:
        condition.isValid = false;
        break;
    }

    return condition;
  }

  /**
   * Trouve la valeur correcte du champ selon le contexte duplicable.
   */
  findCorrectFieldValue(fieldValue: any, fieldOccurrenceCount: number, condition: SimpleCondition, elementId: string): any {
    const fieldSection = this.getDuplicableSectionFromId(condition.field);

    if (Array.isArray(fieldValue) && condition.parentIndex !== undefined) {
      return fieldValue[condition.parentIndex];
    }

    // 🔧 FIX 2026-04-22: Pour les champs duplicables, appeler directement getEnrichedFieldValue
    // au lieu de dépendre de fieldValue étant un Array. En effet, getFieldValueForPath peut
    // retourner null/scalaire (ex: quand un seul lot est dans flatFields au moment de l'appel)
    // même si d'autres lots ont des valeurs. On lit toujours le duplicableMapping frais.
    if (fieldSection && fieldSection === condition.duplicableSectionId) {
      const enrichedValue = this.getEnrichedFieldValue(condition.field);
      if (enrichedValue.isDuplicable && enrichedValue.duplicableMapping) {
        const correctElementId = this.buildCorrectElementIdForMapping(fieldSection, elementId);
        const valuesForElement = enrichedValue.duplicableMapping.get(correctElementId);
        return valuesForElement && valuesForElement.length > 0
          ? (valuesForElement.length === 1 ? valuesForElement[0] : valuesForElement)
          : null;
      }
      // Pas de mapping duplicable : si fieldValue est un Array, utiliser l'index
      if (Array.isArray(fieldValue)) {
        return fieldValue[elementId.split('_')[1]];
      }
    }

    return fieldValue;
  }

  /**
   * Recherche une section duplicable dans un identifiant.
   */
  getDuplicableSectionFromId(id: string): string | undefined {
    if (!id) return undefined;

    const enumValues = Object.values(DuplicableSectionEnum);

    return enumValues.find(section => {
      const pattern = new RegExp(`-${section}(-|$)`);
      return pattern.test(id);
    })?.toString();
  }

  private buildCorrectElementIdForMapping(fieldSection: string, elementId: string): string {
    const sectionToTypeMap: Record<string, string> = {
      'LotResult': 'RES',
      'Organization': 'ORG',
      'Organization-Company': 'ORG',
      'Contract': 'CON',
      'Tender': 'TEN',
      'Tenderer': 'TPA',
      'Lot': 'LOT',
      'Part': 'PAR'
    };

    const duplicableType = sectionToTypeMap[fieldSection];

    if (!duplicableType) {
      return elementId;
    }

    // Extraire l'index depuis elementId (format: TYPE_INDEX, ex: ORG_2)
    const parts = elementId.split('_');
    if (parts.length >= 2) {
      const elementType = parts[0];
      const elementIndex = parseInt(parts[1], 10);

      // Si le type de l'elementId correspond au type recherché, utiliser son index
      if (elementType === duplicableType && !isNaN(elementIndex)) {
        return `${duplicableType}_${elementIndex}`;
      }
    }

    // Pour le format GR-...-Section_X_Y (ex: GR-Organisations-Section_0_0),
    // l'index local (X) est toujours 0 car les éléments sont chargés un par un.
    // On doit utiliser getDisplayedElementIndex qui est mis à jour quand on ouvre un accordéon,
    // contrairement à actuaEntityIndex qui n'est mis à jour que lors d'un chargement backend.
    const sectionMatch = elementId.match(/Section_(\d+)(?:_\d+)?$/);
    if (sectionMatch) {
      // Priorité à getDisplayedElementIndex car il est synchronisé avec l'accordéon ouvert
      const displayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(duplicableType);
      if (displayedIndex >= 0) {
        return `${duplicableType}_${displayedIndex}`;
      }

      // Fallback sur l'index extrait du format Section_X_Y (utile pour les cas où aucun accordéon n'est ouvert)
      const extractedIndex = parseInt(sectionMatch[1], 10);
      if (!isNaN(extractedIndex)) {
        return `${duplicableType}_${extractedIndex}`;
      }
    }

    // Fallback final: utiliser getDisplayedElementIndex si disponible
    const displayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(duplicableType);
    const result = displayedIndex >= 0
      ? `${duplicableType}_${displayedIndex}`
      : elementId;


    return result;
  }

  private normalizeMeasuredFieldIfPeriodRight(fieldId: string, fieldValue: any, rightIsPeriod: boolean): any {
    if (!rightIsPeriod) return fieldValue;

    const n = Number(fieldValue);
    if (isNaN(n)) return fieldValue;

    const fields = this.getFlatFieldsValue().get(fieldId) || [];
    const displayed = fields.find(f => f.isDisplayed) ?? fields[0];
    const unit = displayed?.unitCodeValue ?? null;

    const normalized = this.measureToDays(n, unit);
    return (normalized ?? fieldValue);
  }

  private measureToDays(value: number, unitCode: string | null): number | null {
    if (value == null || isNaN(+value)) return null;
    if (!unitCode) return null;

    const u = ('' + unitCode).trim().toLowerCase();
    if (u === 'y' || u.startsWith('year') || u.startsWith('an')) return value * 365;
    if (u === 'm' || u.startsWith('mon')) return value * 30;
    if (u === 'w' || u.startsWith('sem') || u.startsWith('week')) return value * 7;
    if (u === 'd' || u.startsWith('jour') || u.startsWith('day')) return value;

    return null;
  }

  /**
   * 🔧 FIX 2026-03-17: Vérifie si l'opérateur est un opérateur de comptage (COUNT_*).
   */
  private isCountOperator(operator: string): boolean {
    return operator != null && operator.startsWith('COUNT_');
  }

  private isEmpty(value: any): boolean {
    return value === undefined || value === null || value === '' || value === 'null';
  }

  private toArray<T>(value: T | T[]): T[] {
    return Array.isArray(value) ? value : [value];
  }

  private isArrayString(val: string): boolean {
    return typeof val === 'string' && /^\[\s*'.*'\s*]$/.test(val);
  }

  private isPeriodValue(value: any): boolean {
    if (typeof value !== 'string') return false;
    const cleaned = value.replace(/^'+|'+$/g, '');
    return /^P(\d+Y)?(\d+M)?(\d+D)?$/.test(cleaned);
  }

  /**
   * Vérifie si une valeur est un champ (inputId).
   */
  isField(value: any): boolean {
    return typeof value === 'string' && /^[A-Z]{2,}-\d{3,}-[A-Za-z0-9\-]+$/.test(value);
  }

  /**
   * Parse une chaîne représentant un tableau en un tableau de chaînes.
   */
  parseArrayString(str: string): string[] {
    try {
      const cleaned = str.replace(/'/g, '"');
      const parsed = JSON.parse(cleaned);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  private parsePeriod(period: string): number {
    const cleaned = period.replace(/^'+|'+$/g, '');
    const match = cleaned.match(/^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?$/);

    if (match) {
      const years = parseInt(match[1] || '0', 10);
      const months = parseInt(match[2] || '0', 10);
      const days = parseInt(match[3] || '0', 10);

      return years * 365 + months * 30 + days;
    }

    return 0;
  }

  /**
   * Indique si la valeur d'une condition est un mot-clé de codelist pays,
   * c'est-à-dire une liste de codes à résoudre depuis les libellés i18n
   * et non une valeur littérale.
   */
  private isCodelistKeyword(conditionValue: any): boolean {
    return conditionValue === 'nuts-country' || conditionValue === 'postcode-country';
  }

  /**
   * Résout un mot-clé de codelist pays ('nuts-country', 'postcode-country')
   * en liste de codes alpha-3, depuis les clés de traduction 'eform-not-editable'.
   */
  private getCodelistCountryCodes(keyword: string): string[] {
    const cached = this.codelistCodesCache.get(keyword);
    if (cached != null && cached.length !== 0) {
      return cached;
    }

    try {
      const currentLang = this.translateService.currentLang || 'fr';
      const translations = this.translateService.translations[currentLang]["eform-not-editable"] || {};
      const codes: string[] = [];
      const prefix = 'code|name|' + keyword + '.';

      Object.keys(translations).forEach(key => {
        if (key.startsWith(prefix)) {
          const countryCode = key.substring(prefix.length);
          if (/^[A-Z]{3}$/.test(countryCode)) {
            codes.push(countryCode);
          }
        }
      });

      codes.sort();
      this.codelistCodesCache.set(keyword, codes);
      return codes;
    } catch (error) {
      return [];
    }
  }
}

